"use client";

import { useEffect, useState } from "react";
import { LanguageProvider } from "@/lib/i18n/LanguageContext";
import { isLocale, localeDir, PREFIXED_LOCALES, type Locale } from "@/lib/i18n/translations";
import { NotFoundContent } from "@/components/NotFoundContent";

/**
 * The 404 of the whole site (app/not-found.tsx). Next renders this root page
 * for every notFound() and unknown URL, outside the [lang] layout, and puts it
 * into every page's payload; so it must not read cookies on the server (that
 * made every page dynamic). It starts in German and switches to the visitor's
 * language in the browser: URL prefix first, then the locale cookie.
 */
function browserLocale(): Locale {
  const path = window.location.pathname;
  const prefixed = PREFIXED_LOCALES.find((l) => path === `/${l}` || path.startsWith(`/${l}/`));
  if (prefixed) return prefixed;
  const cookie = document.cookie.match(/(?:^|; )dw\.locale=([^;]*)/)?.[1];
  return isLocale(cookie) ? cookie : "de";
}

export function RootNotFound() {
  const [locale, setLocale] = useState<Locale>("de");
  useEffect(() => {
    const l = browserLocale();
    document.documentElement.lang = l;
    document.documentElement.dir = localeDir(l);
    // eslint-disable-next-line react-hooks/set-state-in-effect -- read once after hydration
    setLocale(l);
  }, []);
  return (
    <LanguageProvider key={locale} initialLocale={locale}>
      <main className="flex-1 bg-cream-50">
        <NotFoundContent />
      </main>
    </LanguageProvider>
  );
}
